import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

export default function MigrationResume() {
  const { user } = useUser();
  const [estadoActual, setEstadoActual] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verificarEstado = async () => {
      try {
        // Verificar cuántos registros tenemos de cada tipo
        const [inmobiliariasSnap, asesoresSnap, asociadosSnap, propiedadesSnap] = await Promise.all([
          getDocs(collection(db, 'inmobiliaria')),
          getDocs(collection(db, 'asesor')),
          getDocs(collection(db, 'asociado')),
          getDocs(collection(db, 'propiedades'))
        ]);

        // Analizar propiedades por agencia
        const propiedadesPorAgencia: { [key: string]: number } = {};
        propiedadesSnap.docs.forEach(doc => {
          const data = doc.data();
          const agenciaId = data.agencia_info?.inmobiliaria_id;
          if (agenciaId) {
            propiedadesPorAgencia[agenciaId] = (propiedadesPorAgencia[agenciaId] || 0) + 1;
          }
        });

        // Verificar propiedades sin agencia
        const propiedadesSinAgencia = propiedadesSnap.docs.filter(doc => {
          const data = doc.data();
          return !data.agencia_info?.inmobiliaria_id;
        }).length;

        setEstadoActual({
          inmobiliarias: inmobiliariasSnap.size,
          asesores: asesoresSnap.size,
          asociados: asociadosSnap.size,
          propiedades: propiedadesSnap.size,
          propiedadesPorAgencia,
          propiedadesSinAgencia,
          totalEsperadoPropiedades: 3432 // Del CSV
        });

      } catch (error) {
        console.error('Error verificando estado:', error);
      } finally {
        setLoading(false);
      }
    };

    verificarEstado();
  }, []);

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ver el estado de migración.</p>
        </div>
      </div>
    );
  }

  const getColorForPercentage = (current: number, total: number) => {
    const percentage = (current / total) * 100;
    if (percentage >= 90) return 'bg-success';
    if (percentage >= 70) return 'bg-warning';
    return 'bg-danger';
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Verificando estado...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-info text-white">
              <h3 className="mb-0">📊 Estado Actual de Migración AMPI</h3>
              <small>Resumen completo de datos migrados y análisis de completitud</small>
            </div>
            <div className="card-body">
              
              {estadoActual && (
                <>
                  {/* Resumen principal */}
                  <div className="row mb-4">
                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-header bg-info text-white">
                          <h6 className="mb-0">🏢 Inmobiliarias</h6>
                        </div>
                        <div className="card-body">
                          <h3 className="text-info">{estadoActual.inmobiliarias}</h3>
                          <small className="text-muted">agencias</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-header bg-primary text-white">
                          <h6 className="mb-0">🏘️ Asesores</h6>
                        </div>
                        <div className="card-body">
                          <h3 className="text-primary">{estadoActual.asesores}</h3>
                          <small className="text-muted">afiliados</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className="card-header bg-success text-white">
                          <h6 className="mb-0">👨‍💼 Asociados</h6>
                        </div>
                        <div className="card-body">
                          <h3 className="text-success">{estadoActual.asociados}</h3>
                          <small className="text-muted">brokers</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="card text-center">
                        <div className={`card text-center ${getColorForPercentage(estadoActual.propiedades, estadoActual.totalEsperadoPropiedades).replace('bg-', 'border-')}`}>
                          <div className={`card-header text-white ${getColorForPercentage(estadoActual.propiedades, estadoActual.totalEsperadoPropiedades)}`}>
                            <h6 className="mb-0">🏠 Propiedades</h6>
                          </div>
                          <div className="card-body">
                            <h3>{estadoActual.propiedades}</h3>
                            <small className="text-muted">
                              de {estadoActual.totalEsperadoPropiedades} esperadas
                            </small>
                            <div className="progress mt-2" style={{ height: '8px' }}>
                              <div 
                                className={`progress-bar ${getColorForPercentage(estadoActual.propiedades, estadoActual.totalEsperadoPropiedades)}`}
                                style={{ 
                                  width: `${(estadoActual.propiedades / estadoActual.totalEsperadoPropiedades) * 100}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Análisis de completitud */}
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card border-warning">
                        <div className="card-header bg-warning text-white">
                          <h5 className="mb-0">⚠️ Estado de Migración de Propiedades</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <h6>📈 Progreso General</h6>
                              <p>
                                <strong>Migradas:</strong> {estadoActual.propiedades} propiedades<br />
                                <strong>Faltantes:</strong> {estadoActual.totalEsperadoPropiedades - estadoActual.propiedades} propiedades<br />
                                <strong>Progreso:</strong> {((estadoActual.propiedades / estadoActual.totalEsperadoPropiedades) * 100).toFixed(1)}%
                              </p>
                            </div>
                            <div className="col-md-6">
                              <h6>🔗 Vinculación</h6>
                              <p>
                                <strong>Con agencia:</strong> {estadoActual.propiedades - estadoActual.propiedadesSinAgencia}<br />
                                <strong>Sin agencia:</strong> {estadoActual.propiedadesSinAgencia}<br />
                                <strong>Agencias con propiedades:</strong> {Object.keys(estadoActual.propiedadesPorAgencia).length}
                              </p>
                            </div>
                          </div>

                          {estadoActual.propiedades < estadoActual.totalEsperadoPropiedades && (
                            <div className="alert alert-info mt-3">
                              <h6>🔄 Recomendación</h6>
                              <p>
                                La migración de propiedades parece estar incompleta. 
                                Puedes continuar desde donde se quedó usando el sistema optimizado.
                              </p>
                              <a href="/ampi-migration-v2" className="btn btn-info">
                                <i className="fa-solid fa-play me-2"></i>
                                Continuar Migración
                              </a>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card border-info">
                        <div className="card-header bg-info text-white">
                          <h6 className="mb-0">🛠️ Herramientas de Administración</h6>
                        </div>
                        <div className="card-body">
                          <div className="d-grid gap-2">
                            <a href="/ampi-migration-v2" className="btn btn-outline-primary">
                              <i className="fa-solid fa-database me-2"></i>
                              Sistema de Migración
                            </a>
                            <a href="/integrity-validator" className="btn btn-outline-success">
                              <i className="fa-solid fa-check-circle me-2"></i>
                              Validar Integridad
                            </a>
                            <a href="/database-cleanup" className="btn btn-outline-danger">
                              <i className="fa-solid fa-trash me-2"></i>
                              Limpieza de DB
                            </a>
                            <a href="/database-analysis" className="btn btn-outline-info">
                              <i className="fa-solid fa-magnifying-glass me-2"></i>
                              Análisis Completo
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top agencias con más propiedades */}
                  {Object.keys(estadoActual.propiedadesPorAgencia).length > 0 && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-secondary text-white">
                            <h6 className="mb-0">🏆 Top Agencias por Número de Propiedades</h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {Object.entries(estadoActual.propiedadesPorAgencia)
                                .sort(([,a], [,b]) => (b as number) - (a as number))
                                .slice(0, 6)
                                .map(([agenciaId, count]) => (
                                  <div key={agenciaId} className="col-md-4 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                      <span className="small">{agenciaId}</span>
                                      <span className="badge bg-primary">{count as number}</span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}